import { isAxiosError } from "axios";
import React, { useContext, useEffect } from "react";

import { useAppAuth } from "src/hooks/useAppAuth";
import { QuestionName, StageName } from "src/hooks/useQuestions";
import { useSavedAnswers } from "src/hooks/useSavedAnswers";

export type AnswersValue = string | string[] | boolean;
export type Answers = Record<QuestionName, AnswersValue>;

export interface Store {
  questionIndex: number;
  answers: Answers;
  progress: Record<StageName, boolean>;
  progressStage?: StageName;
  uuid?: string;
  completedAt?: string;
  isLoading: boolean;
  isError: boolean;
}

type StoreAction =
  | { type: "initialised"; payload: Partial<Store> }
  | {
      type: "update-answers";
      payload: Store["answers"];
    }
  | { type: "cancel" | "next" | "back" }
  | {
      type: "update-progress";
      payload: Partial<Store["progress"]>;
    }
  | { type: "update-progress-stage"; payload: Store["progressStage"] }
  | { type: "update-completed-at"; payload: Store["completedAt"] }
  | {
      type: "uuid";
      payload: string;
    }
  | { type: "navigate"; payload: Store["questionIndex"] };

export type StoreDispatch = React.Dispatch<StoreAction>;

const initialState: Store = {
  questionIndex: 0,
  answers: {},
  progress: {
    skin_profiler: false,
    about: false,
    skin_conditions: false,
  },
  progressStage: undefined,
  uuid: undefined,
  isLoading: true,
  isError: false,
};

interface StoreState {
  store: Store;
  dispatch: StoreDispatch;
}

const StoreContext = React.createContext<StoreState>({
  store: initialState,
  dispatch: () => {
    throw new Error("You forgot to wrap your component in <StoreProvider>.");
  },
});

function reducer(state: Store, action: StoreAction) {
  switch (action.type) {
    case "initialised": {
      return { ...state, ...action.payload, isLoading: false };
    }
    case "update-answers":
      return {
        ...state,
        answers: { ...state.answers, ...action.payload },
        completedAt: undefined,
      };
    case "cancel":
      return initialState;
    case "update-progress":
      return { ...state, progress: { ...state.progress, ...action.payload } };
    case "update-progress-stage":
      return { ...state, progressStage: action.payload };
    case "update-completed-at": {
      return { ...state, completedAt: action.payload };
    }
    case "uuid":
      const updated = { ...state, uuid: action.payload };
      return updated;
    case "next": {
      return {
        ...state,
        questionIndex: state.questionIndex + 1,
      };
    }
    case "back": {
      return {
        ...state,
        questionIndex: state.questionIndex - 1,
      };
    }
    case "navigate": {
      return { ...state, questionIndex: action.payload };
    }
    default:
      throw new Error();
  }
}

export function StoreProvider({ children }: React.PropsWithChildren) {
  const [store, dispatch] = React.useReducer(reducer, initialState);
  const { personId } = useAppAuth();
  const { data, error } = useSavedAnswers();

  useEffect(() => {
    if (data) {
      dispatch({
        type: "initialised",
        payload: { ...data, isError: false },
      });
      return;
    }

    if (error) {
      dispatch({
        type: "initialised",
        payload: {
          isError: !isAxiosError(error) || error.response?.status !== 404,
        },
      });
    }
  }, [data, error, dispatch]);

  useEffect(() => {
    if (!store.isLoading && personId) {
      sessionStorage.setItem(`store:${personId}`, JSON.stringify(store));
    }
  }, [store, personId]);

  return <StoreContext.Provider value={{ store, dispatch }}>{children}</StoreContext.Provider>;
}

export const useStore = () => useContext(StoreContext);
